import { useEffect, useState } from "react";
import { Check, Smartphone } from "lucide-react";

/**
 * Premium "Payment Success" celebration overlay.
 *
 * Props:
 *  - provider     : "airtel" | "moov"
 *  - amountLabel  : pre-formatted string e.g. "1 346 FCFA"
 *  - reference    : transaction reference, shown in mono
 *  - onDone()     : called after the animation completes (≈ 2.4 s)
 *
 * The component renders a full-screen overlay with a 4-phase animation:
 *   1. backdrop fade-in
 *   2. dark disc zooms in with the brand color
 *   3. checkmark draws via stroke-dashoffset
 *   4. text + reference fade-up, then onDone() fires
 */
export default function PaymentSuccessOverlay({ provider, amountLabel, reference, onDone }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 60);    // disc zoom
    const t2 = setTimeout(() => setPhase(2), 480);   // check draw
    const t3 = setTimeout(() => setPhase(3), 1100);  // text up
    const t4 = setTimeout(() => onDone && onDone(), 2400);
    return () => { [t1, t2, t3, t4].forEach(clearTimeout); };
  }, [onDone]);

  const providerLabel = provider === "moov" ? "Moov Money" : "Airtel Money";
  const accent = provider === "moov" ? "#003DA5" : "#E60000";

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center px-6"
      style={{
        backgroundColor: "rgba(15, 15, 15, 0.94)",
        backdropFilter: "blur(8px)",
        animation: "kanama-fade-in 220ms ease-out both",
      }}
      role="dialog"
      aria-live="polite"
      data-testid="payment-success-overlay"
    >
      {/* Confetti specks */}
      {phase >= 2 && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          {Array.from({ length: 18 }).map((_, i) => {
            const delay = 80 + i * 35;
            const angle = (i / 18) * 360;
            const distance = 110 + (i % 4) * 18;
            const colors = ["#C8A04D", "#B25E3F", "#2D5A3D", "#E8C977", accent];
            return (
              <span
                key={i}
                className="absolute top-1/2 left-1/2 w-1.5 h-1.5 rounded-full"
                style={{
                  backgroundColor: colors[i % colors.length],
                  transform: `translate(-50%, -50%) rotate(${angle}deg) translateY(0px)`,
                  animation: `kanama-confetti 900ms cubic-bezier(.18,.78,.34,1) ${delay}ms both`,
                  ["--kn-d"]: `${distance}px`,
                }}
              />
            );
          })}
        </div>
      )}

      {/* Disc + check */}
      <div
        className="relative"
        style={{
          transform: phase >= 1 ? "scale(1)" : "scale(0.55)",
          opacity: phase >= 1 ? 1 : 0,
          transition: "transform 380ms cubic-bezier(.34,1.6,.6,1), opacity 220ms ease-out",
        }}
        data-testid="payment-success-disc"
      >
        <div
          className="w-32 h-32 rounded-full flex items-center justify-center shadow-[0_18px_60px_rgba(200,160,77,0.45)]"
          style={{ backgroundColor: "#C8A04D" }}
        >
          <svg
            viewBox="0 0 24 24"
            className="w-16 h-16"
            fill="none"
            stroke="#0F0F0F"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            data-testid="payment-success-check"
          >
            <path
              d="M4 12.5 L10 18 L20 6"
              style={{
                strokeDasharray: 28,
                strokeDashoffset: phase >= 2 ? 0 : 28,
                transition: "stroke-dashoffset 520ms cubic-bezier(.65,0,.35,1)",
              }}
            />
          </svg>
        </div>

        {/* Soft pulsing ring */}
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            boxShadow: "0 0 0 0 rgba(200,160,77,0.45)",
            animation: phase >= 2 ? "kanama-pulse 1400ms ease-out 280ms" : "none",
          }}
          aria-hidden="true"
        />
      </div>

      {/* Text */}
      <div
        className="mt-8 text-center max-w-sm"
        style={{
          transform: phase >= 3 ? "translateY(0)" : "translateY(14px)",
          opacity: phase >= 3 ? 1 : 0,
          transition: "transform 360ms ease-out, opacity 360ms ease-out",
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold mb-3"
          style={{ backgroundColor: `${accent}22`, color: "#F4E2BB" }}
          data-testid="payment-success-provider"
        >
          <Smartphone className="w-3 h-3" />
          {providerLabel}
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white tracking-tight mb-1">
          Paiement confirmé
        </h2>
        <div className="font-heading text-3xl sm:text-4xl font-bold text-brand-gold" data-testid="payment-success-amount">
          {amountLabel}
        </div>
        {reference && (
          <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-white/55 font-mono" data-testid="payment-success-reference">
            Réf. {reference}
          </div>
        )}
        <p className="mt-4 text-sm text-white/75 flex items-center justify-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
          Recherche d'un chauffeur…
        </p>
      </div>

      {/* Inline keyframes (so this component is fully self-contained) */}
      <style>{`
        @keyframes kanama-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes kanama-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(200,160,77,0.55); }
          100% { box-shadow: 0 0 0 64px rgba(200,160,77,0);    }
        }
        @keyframes kanama-confetti {
          0%   { transform: translate(-50%, -50%) rotate(var(--kn-angle, 0deg)) translateY(0px) scale(.6); opacity: 1; }
          100% { transform: translate(-50%, -50%) rotate(var(--kn-angle, 0deg)) translateY(var(--kn-d, 120px)) scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
